/**
 * How the Scheduled tasks thread is laid out: date separators between days, and the older results
 * folded into one summary row.
 *
 * Lives outside MessengerWindow, and pure, for the reason reyConversationCap.js does: this is list
 * arithmetic with several edge cases (an empty thread, one day, nothing old enough to fold, a fold
 * with fewer than the peek size left in it) and none of them should need a mounted component to
 * assert. The component keeps one boolean; everything else is derived here.
 *
 * ONLY the scheduled thread uses this. Every other thread keeps the plain message loop it has always
 * had — see the guard at the call site, which is the whole safety argument for touching a render path
 * that all threads share.
 */

/**
 * How many of the newest results stay visible. Ten rather than "everything from today", because a
 * quiet day would then show one result above a summary of nineteen, and a busy one would fold
 * nothing — the boundary should not swing on how the dealership's day happened to go.
 */
export const RECENT_VISIBLE = 10;

/**
 * How many of the folded results the expand control reveals. Five, from the spec: "controls to expand
 * the five most recent of the earlier runs". Anything older than those five is reachable only through
 * full run history, which is the other control on the same row.
 */
export const EARLIER_PEEK = 5;

/**
 * The calendar day a row belongs under, as YYYY-MM-DD in the VIEWER's zone.
 *
 * Keyed on the message's own `sentAt`, NOT on the run time the card displays. Those differ whenever
 * quiet hours deferred a run, and the list is ordered by sentAt — so grouping by run time would put a
 * separator out of sequence with the messages beneath it. The card still states when the run happened;
 * the separator says when the result arrived.
 *
 * Returns null for anything undated, which callers must treat as "no separator" rather than as a day.
 */
export const dayKeyOf = message => {
  const raw = message?.sentAt;
  if (!raw) return null;

  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return null;

  // Local parts, not toISOString() — that converts to UTC first, so a result delivered at 7pm Central
  // would file under tomorrow.
  const month = `${when.getMonth() + 1}`.padStart(2, '0');
  const day = `${when.getDate()}`.padStart(2, '0');
  return `${when.getFullYear()}-${month}-${day}`;
};

/**
 * What the collapsed row stands for, or null when nothing is old enough to fold.
 *
 * `count` and the range describe exactly the rows being hidden ON SCREEN — never a claim about the
 * whole history. The thread is served its 20 most recent messages and cannot ask for more, so a total
 * here would be a number this code has no way to know. The full-history control is what answers that.
 */
export const earlierSummary = (messages, recentVisible = RECENT_VISIBLE) => {
  const rows = messages || [];
  if (rows.length <= recentVisible) return null;

  const earlier = rows.slice(0, rows.length - recentVisible);
  const dated = earlier.map(dayKeyOf).filter(Boolean);

  return {
    count: earlier.length,
    // Null when none of the folded rows carried a usable date — the row then states its count alone
    // rather than a range it had to invent.
    fromDay: dated.length ? dated[0] : null,
    toDay: dated.length ? dated[dated.length - 1] : null,
  };
};

/**
 * The thread as rows to render, oldest first.
 *
 * Row shapes:
 *   { type: 'earlier', count, fromDay, toDay, expanded, hiddenBeyondPeek }
 *   { type: 'separator', key, day }
 *   { type: 'message', key, message }
 *
 * Separators are emitted only where the day actually changes, so a thread whose results all landed on
 * one day gets exactly one. Dates are formatted by the component, not here — this module deals in day
 * keys so its own tests do not depend on a locale.
 *
 * @param messages Oldest→newest, as messages_displayed already provides.
 * @param expanded Whether the earlier row has been opened to reveal its peek.
 */
export const buildScheduledRows = (messages, { expanded = false, recentVisible = RECENT_VISIBLE, peek = EARLIER_PEEK } = {}) => {
  const all = messages || [];
  const summary = earlierSummary(all, recentVisible);

  // Nothing folded: every message renders, separators only.
  if (!summary) return withSeparators(all);

  const earlier = all.slice(0, all.length - recentVisible);
  const recent = all.slice(all.length - recentVisible);

  // The peek is the NEWEST of the folded rows — the ones nearest what is already on screen, which is
  // what "the five most recent of the earlier runs" means.
  const peeked = expanded ? earlier.slice(Math.max(0, earlier.length - peek)) : [];

  const earlierRow = {
    type: 'earlier',
    count: summary.count,
    fromDay: summary.fromDay,
    toDay: summary.toDay,
    expanded,
    // Whether opening the peek still leaves some behind. Drives whether the expanded state keeps
    // pointing at full history, so the control does not disappear while results are still unseen.
    hiddenBeyondPeek: Math.max(0, summary.count - peek),
  };

  return [earlierRow, ...withSeparators([...peeked, ...recent])];
};

/**
 * Interleaves day separators into an already-ordered run of messages.
 *
 * Deliberately separate and not exported: it is the one piece of buildScheduledRows that is pure
 * mechanics, and keeping it here means the peeked rows and the recent rows get separators from the
 * same code rather than from two call sites that could disagree about the boundary.
 */
const withSeparators = messages => {
  const rows = [];
  let lastDay = null;

  (messages || []).forEach(message => {
    const day = dayKeyOf(message);
    if (day && day !== lastDay) {
      rows.push({ type: 'separator', key: `sep_${day}`, day });
      lastDay = day;
    }
    rows.push({ type: 'message', key: `msg_${message.id}`, message });
  });

  return rows;
};
